import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { validateSession } from '../utils/functions';

// Reponer en estantería — la rutina de piso que cierra el hueco que deja una venta de mostrador.
//
// Se vende un juego, sale de la estantería, y nadie trae otra copia del depósito. El sistema ya
// sabía qué se había vendido; lo que faltaba era la LISTA DE TRABAJO: qué falta reponer, en qué
// orden, y un lugar donde marcar lo hecho.
//
// Decisiones de UX, todas por el mismo motivo (esto se usa CAMINANDO, con el celular en una mano
// y una caja en la otra):
//   · Una tarjeta por PRODUCTO, no por venta. El empleado camina hasta un lugar de la estantería
//     y deja ahí las copias que falten; dos filas del mismo juego lo hacen caminar dos veces.
//   · Un botón grande por tarjeta, sin checkboxes ni "guardar". El trabajo es de a uno.
//   · Deshacer en un toast de 8 segundos. El botón es de un toque: sin vuelta atrás, un toque
//     accidental saca el juego de la lista y el hueco queda para siempre y sin rastro.
//   · Se marca en el acto y la tarjeta se va sola (optimista). Esperar al servidor con la lista
//     congelada se lee como que el botón no anduvo, y se toca de nuevo.
const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

// Ventana para arrepentirse. 8 s y no 3: el que marca sigue caminando y recién a los pocos pasos
// se da cuenta de que marcó el juego equivocado.
const UNDO_MS = 8000;
// El sidebar cachea su badge en sessionStorage (SHELF_BADGE_CACHE_KEY). Al marcar acá, ese número
// queda viejo hasta un minuto: se ve la lista bajar y el menú clavado. Se invalida la cache.
const SIDEBAR_BADGE_EVENT = 'shelf-restock-changed';

// Invalidar la cache no alcanzaba: el sidebar YA está montado en esta pantalla y no vuelve a leer
// hasta su intervalo de 3 minutos. Se marcaban 5 juegos, el encabezado bajaba a 2 y el menú seguía
// diciendo 7. El evento lo actualiza en el acto; el `removeItem` sigue, para que la próxima
// navegación tampoco encuentre el número viejo.
function avisarBadge(products) {
    try {
        window.dispatchEvent(new CustomEvent(SIDEBAR_BADGE_EVENT, { detail: { products } }));
    } catch (e) {
        // CustomEvent no soportado: el badge se refresca solo cuando vence su TTL.
    }
}

// 'YYYY-MM-DD HH:mm:ss' -> partes, sin que la zona del navegador opine. `new Date(string)` sobre
// un literal sin offset corre el día en la madrugada, y acá el día es justamente la agrupación.
function parseWallClock(value) {
    if (!value || typeof value !== 'string') {
        return null;
    }
    const [date, time] = value.split(' ');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return null;
    }
    return { date, time: (time || '').slice(0, 5) };
}

function todayWallClock(offsetDays = 0) {
    const cursor = new Date();
    cursor.setDate(cursor.getDate() - offsetDays);
    const mes = String(cursor.getMonth() + 1).padStart(2, '0');
    const dia = String(cursor.getDate()).padStart(2, '0');
    return `${cursor.getFullYear()}-${mes}-${dia}`;
}

function dayLabel(date) {
    // Producción tiene ventas con `date = '0000-00-00'` (8 al 31/08/2026, 7 de ellas de
    // mostrador). Hoy el piso de la lista las deja afuera, pero si alguna vez entra una fecha que
    // no parsea, el encabezado del grupo saldría "Invalid Date undefined/fecha/si" — feo y sin
    // ningún error que lo delate. Mejor decir lo que pasa.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date))) return 'Sin fecha';
    if (date === todayWallClock(0)) return 'Hoy';
    if (date === todayWallClock(1)) return 'Ayer';
    const [year, month, day] = date.split('-');
    // Date en UTC + formateo en UTC: los dígitos que entran son los que salen.
    const nombre = new Date(`${date}T00:00:00Z`).toLocaleDateString('es-UY', { weekday: 'long', timeZone: 'UTC' });
    return `${nombre} ${day}/${month}/${year.slice(2)}`;
}

function diasDesde(date) {
    const desde = new Date(`${date}T00:00:00Z`);
    const hoy = new Date(`${todayWallClock(0)}T00:00:00Z`);
    return Math.round((hoy - desde) / 86400000);
}

class ShelfRestockPage extends Component {

    constructor() {
        super();
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: Number(localStorage.getItem('role') || 0),
            items: [],
            summary: null,
            doneToday: [],
            showDone: false,
            loading: true,
            error: null,
            // { lines, name } de lo último marcado, mientras se puede deshacer.
            undoable: null,
        };
        this.undoTimer = null;
        // Los `await fetch` pueden volver después de que el usuario se fue a otra pantalla.
        this._montado = false;
        this.marcaSeq = 0;
    }

    async componentDidMount() {
        this._montado = true;
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        await this.load();
    }

    componentWillUnmount() {
        this._montado = false;
        clearTimeout(this.undoTimer);
    }

    fetchJson = async (path, options) => {
        const res = await fetch(apiBase() + path, { headers: authHeaders(), ...options });
        return res.json();
    }

    load = async () => {
        try {
            const body = await this.fetchJson('shelf-restock/pending', { method: 'GET' });
            if (!this._montado) {
                return;
            }
            if (body.error === 0) {
                avisarBadge(body.data.summary ? body.data.summary.products : null);
                this.setState({ items: body.data.items || [], summary: body.data.summary, loading: false, error: null });
            } else {
                this.setState({ error: 'No se pudo cargar la lista de reposición', loading: false });
            }
        } catch (e) {
            if (this._montado) {
                this.setState({ error: 'Error de red', loading: false });
            }
        }
    }

    loadDoneToday = async () => {
        try {
            const body = await this.fetchJson('shelf-restock/done-today', { method: 'GET' });
            if (body.error === 0 && this._montado) {
                this.setState({ doneToday: body.data.items || [] });
            }
        } catch (e) {
            // El historial del día es informativo: si falla, la lista de trabajo sigue sirviendo.
        }
    }

    toggleDone = async () => {
        const showDone = !this.state.showDone;
        this.setState({ showDone });
        if (showDone) {
            await this.loadDoneToday();
        }
    }

    linesOf = (item) => item.lines.map((line) => ({
        purchaseId: line.purchaseId,
        productCode: item.code,
        quantity: line.quantity,
    }));

    // Marca todas las líneas pendientes del producto. La tarjeta se va en el acto y el servidor
    // se entera después: si falla, la lista se recarga y el ítem vuelve solo.
    // Descuenta del resumen SOLO del bloque al que pertenece el ítem.
    //
    // `summary.products/units/overdue` cuenta accionables; lo que espera stock vive en
    // `summary.waitingStock`. Descontar siempre de lo accionable hacía que "Quitar" en el bloque
    // gris bajara un contador que nunca lo había incluido: con 1 para reponer y 2 esperando,
    // quitar uno dejaba el encabezado en 0 con la tarjeta y su botón todavía en pantalla.
    descontarDelResumen = (summary, item) => {
        if (!summary) {
            return null;
        }
        if (item.outOfStock) {
            const espera = summary.waitingStock || { products: 0, units: 0 };
            return {
                ...summary,
                waitingStock: {
                    products: Math.max(0, espera.products - 1),
                    units: Math.max(0, espera.units - item.pendingUnits),
                },
            };
        }
        return {
            ...summary,
            products: Math.max(0, summary.products - 1),
            units: Math.max(0, summary.units - item.pendingUnits),
            overdue: Math.max(0, summary.overdue - (item.overdue ? 1 : 0)),
        };
    }

    // Marca todas las líneas pendientes del producto. La tarjeta se va en el acto y el servidor se
    // entera después: si falla, la lista se recarga y el ítem vuelve solo.
    marcarRepuesto = async (item, { leyenda = null, kind = 'restocked' } = {}) => {
        const lines = this.linesOf(item);
        // Identidad de ESTA acción. Sin ella, el `catch` de una marca que falla borraba el toast
        // de otra marca posterior que sí había andado.
        const token = (this.marcaSeq += 1);

        clearTimeout(this.undoTimer);
        // La promesa se guarda y viaja en el `undoable`: "Deshacer" tiene que ESPERARLA antes de
        // mandar el borrado. Antes salían los dos POST en paralelo y, con red de celular, el
        // orden de llegada decidía el resultado — el undo podía borrar cero filas y el done
        // insertar después, dejando el juego marcado como repuesto con el hueco todavía ahí.
        const pedido = this.fetchJson('shelf-restock/done', {
            method: 'POST',
            body: JSON.stringify({ lines, kind }),
        });

        this.setState((s) => {
            const summary = this.descontarDelResumen(s.summary, item);
            avisarBadge(summary ? summary.products : null);
            return {
                items: s.items.filter((x) => x.code !== item.code),
                summary,
                undoable: { token, pedido, kind, name: item.name, leyenda },
            };
        });
        this.undoTimer = setTimeout(() => this.limpiarToast(token), UNDO_MS);

        try {
            const body = await pedido;
            if (body.error !== 0) {
                this.actualizar({ error: 'No se pudo guardar. Se recargó la lista.' }, token);
                await this.load();
            } else if (this.state.showDone) {
                await this.loadDoneToday();
            }
        } catch (e) {
            this.actualizar({ error: 'Error de red. Se recargó la lista.' }, token);
            await this.load();
        }
    }

    // Cambia el estado sólo si el componente sigue montado, y baja el toast SÓLO si es el de esta
    // acción (`token`).
    actualizar = (cambios, token = null) => {
        if (!this._montado) {
            return;
        }
        this.setState((s) => ({
            ...cambios,
            ...(token != null && s.undoable && s.undoable.token === token ? { undoable: null } : {}),
        }));
    }

    limpiarToast = (token) => {
        if (!this._montado) {
            return;
        }
        this.setState((s) => (s.undoable && s.undoable.token === token ? { undoable: null } : null));
    }

    // Resuelve las líneas igual que "Repuesto", pero se guarda con otro `kind`: no se repuso
    // nada. Sin esa distinción, descartar un discontinuado vendido hace un mes entraría al panel
    // de demora como una reposición de 700 horas y arruinaría la mediana sin que se note.
    quitar = (item) => this.marcarRepuesto(item, { leyenda: `Quitado: ${item.name}`, kind: 'dismissed' });

    deshacer = async () => {
        const undoable = this.state.undoable;
        if (undoable == null) {
            return;
        }
        clearTimeout(this.undoTimer);
        this.setState({ undoable: null });

        try {
            // Esperar el marcado antes de borrarlo: si no, los dos pedidos compiten y el
            // resultado lo decide la red. Además el servidor devuelve las líneas que REALMENTE
            // escribió esta llamada, así que deshacer nunca puede borrar el marcado de otro
            // empleado que llegó primero.
            const marcado = await undoable.pedido;
            const escritas = (marcado && marcado.data && marcado.data.marked) || [];
            if (escritas.length > 0) {
                await this.fetchJson('shelf-restock/undo', {
                    method: 'POST',
                    body: JSON.stringify({ lines: escritas, kind: undoable.kind }),
                });
            }
        } catch (e) {
            // Recargar igual: si el borrado entró y la respuesta se perdió, la lista manda.
        }

        if (!this._montado) {
            return;
        }
        await this.load();
        if (this.state.showDone) {
            await this.loadDoneToday();
        }
    }

    accionables = () => this.state.items.filter((item) => !item.outOfStock);

    esperandoStock = () => this.state.items.filter((item) => item.outOfStock);

    // Agrupa por día de la venta MÁS RECIENTE del producto, que es el orden en el que viene la
    // lista. Los encabezados son la única señal de "esto es de hoy / esto viene arrastrado".
    grupos = () => {
        const grupos = [];
        this.accionables().forEach((item) => {
            const parsed = parseWallClock(item.lastSoldAt);
            const date = parsed ? parsed.date : 'sin-fecha';
            const ultimo = grupos[grupos.length - 1];
            if (ultimo && ultimo.date === date) {
                ultimo.items.push(item);
            } else {
                grupos.push({ date, items: [item] });
            }
        });
        return grupos;
    }

    drawBadges = (item) => {
        const badges = [];
        if (item.pendingUnits > 1) {
            badges.push(
                <span key="qty" className="badge badge-primary mr-1" style={{ fontSize: '13px' }}>
                    {item.pendingUnits} unidades
                </span>
            );
        }
        if (item.overdue) {
            const parsed = parseWallClock(item.firstSoldAt);
            const dias = parsed ? diasDesde(parsed.date) : null;
            badges.push(
                <span key="overdue" className="badge badge-danger mr-1" style={{ fontSize: '13px' }}>
                    {dias != null ? `Atrasado ${dias} días` : 'Atrasado'}
                </span>
            );
        }
        return badges;
    }

    drawItem = (item) => {
        const parsed = parseWallClock(item.lastSoldAt);
        return (
            <div
                key={item.code}
                className="d-flex align-items-center justify-content-between flex-wrap p-3"
                style={{
                    borderBottom: '1px solid #e1e5eb',
                    // El atraso se ve antes de leer: una franja roja al costado de la tarjeta.
                    borderLeft: item.overdue ? '4px solid #c4183c' : '4px solid transparent',
                    background: item.overdue ? '#fdf3f5' : 'transparent',
                }}
            >
                <div style={{ minWidth: '200px', flex: '1 1 240px', paddingRight: '12px' }}>
                    <div style={{ fontSize: '17px', fontWeight: 500, lineHeight: '1.3' }}>{item.name}</div>
                    <small className="text-muted d-block" style={{ marginTop: '2px' }}>
                        {item.barCode ? `${item.barCode} · ` : ''}Vendido {parsed ? parsed.time : ''} h
                        {item.outOfStock ? '' : ` · quedan ${item.stock} en stock`}
                    </small>
                    <div style={{ marginTop: '6px' }}>{this.drawBadges(item)}</div>
                </div>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => this.marcarRepuesto(item)}
                    // Objetivo táctil grande: esto se toca caminando, no sentado con un mouse.
                    style={{ minWidth: '150px', minHeight: '48px', fontSize: '16px', marginTop: '4px' }}
                >
                    <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>check</i>
                    Repuesto
                </button>
            </div>
        );
    }

    /**
     * "Esperando stock": el hueco existe, pero no hay copia para poner.
     *
     * No va en la lista de trabajo porque no es trabajo — mandar a alguien a buscar una copia que
     * no está es la forma más rápida de que la lista deje de creerse. Pero tampoco desaparece: el
     * hueco en la estantería es real y el encargado tiene que saber que ese lugar está vacío (para
     * correr el juego de al lado, poner un cartel, o apurar el pedido al proveedor).
     *
     * No hay nada que "reactivar": `outOfStock` se calcula del stock actual en cada lectura, así
     * que el día que entra la reposición del proveedor el juego salta solo a la lista de arriba.
     * El botón "Quitar" es para el otro caso, el que no vuelve: un juego discontinuado que no se
     * repone nunca y que si no quedaría acá para siempre.
     */
    drawEsperandoStock = () => {
        const items = this.esperandoStock();
        if (items.length === 0) {
            return null;
        }
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="m-0">
                        Esperando stock <span className="text-muted" style={{ fontWeight: 'normal' }}>({items.length})</span>
                    </h6>
                    <small className="text-muted">Vuelven solos a la lista cuando entre stock</small>
                </div>
                <div className="card-body p-0 pb-2">
                    <p className="text-muted px-3 pt-3 mb-2" style={{ fontSize: '13px' }}>
                        Se vendieron y quedó el hueco, pero no hay otra copia en el depósito. No hay
                        nada que reponer hasta que llegue el pedido al proveedor.
                    </p>
                    {items.map((item) => {
                        const parsed = parseWallClock(item.lastSoldAt);
                        return (
                            <div
                                key={item.code}
                                className="d-flex align-items-center justify-content-between flex-wrap px-3 py-2"
                                style={{ borderTop: '1px solid #e1e5eb' }}
                            >
                                <div style={{ minWidth: '200px', flex: '1 1 240px', paddingRight: '12px' }}>
                                    <div style={{ fontSize: '15px', color: '#5a6169' }}>
                                        {item.name}
                                        {item.pendingUnits > 1 ? <span className="text-muted"> · {item.pendingUnits} unidades</span> : null}
                                    </div>
                                    <small className="text-muted">
                                        Vendido {parsed ? `${dayLabel(parsed.date).toLowerCase()} ${parsed.time} h` : ''} · sin stock
                                    </small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-white"
                                    style={{ minHeight: '38px' }}
                                    onClick={() => this.quitar(item)}
                                    title="Para juegos que no se van a reponer nunca (discontinuados)"
                                >
                                    Quitar
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    drawEmpty = () => {
        // Con juegos esperando stock el mensaje NO puede decir que todo volvió a la estantería:
        // hay huecos abiertos, sólo que no se pueden llenar hoy. Un "listo" falso es lo que hace
        // que después nadie crea el "listo" verdadero.
        const esperando = this.esperandoStock().length;
        return (
            <div className="card-body p-5" style={{ textAlign: 'center', color: '#5a6169' }}>
                <i className="material-icons" style={{ fontSize: '48px', color: '#17c671' }}>check_circle_outline</i>
                <h5 style={{ marginTop: '12px' }}>No queda nada para reponer</h5>
                <p className="m-0">
                    {esperando > 0
                        ? `Todo lo que se podía reponer ya volvió a la estantería. Quedan ${esperando} esperando stock, acá abajo.`
                        : 'Todo lo que se vendió en el local ya volvió a la estantería.'}
                </p>
            </div>
        );
    }

    drawDoneToday = () => {
        if (!this.state.showDone) {
            return null;
        }
        const items = this.state.doneToday;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Resueltos hoy</h6>
                </div>
                <div className="card-body p-0 pb-3">
                    {items.length === 0 ? (
                        <p className="p-3 m-0 text-muted">Todavía no se resolvió nada hoy.</p>
                    ) : (
                        <table className="table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Producto</th>
                                    <th className="border-0">Qué pasó</th>
                                    <th className="border-0">Unidades</th>
                                    <th className="border-0">Quién</th>
                                    <th className="border-0">Hora</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => {
                                    const parsed = parseWallClock(row.doneAt);
                                    return (
                                        <tr key={`${row.purchaseId}-${row.code}`}>
                                            <td>{row.name}</td>
                                            {/* Repuesto y quitado salen de la lista igual, pero
                                                no son lo mismo y el registro no puede taparlo:
                                                sólo el primero llenó un hueco. */}
                                            <td>
                                                {row.kind === 'dismissed'
                                                    ? <span className="text-muted">Quitado, sin reponer</span>
                                                    : 'Repuesto'}
                                            </td>
                                            <td>{row.quantity}</td>
                                            <td>{row.doneBy || '—'}</td>
                                            <td>{parsed ? parsed.time : ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }

    drawUndoToast = () => {
        const undoable = this.state.undoable;
        if (undoable == null) {
            return null;
        }
        return (
            <div
                style={{
                    position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
                    background: '#212529', color: '#fff', borderRadius: '6px', padding: '12px 16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 1100, display: 'flex',
                    alignItems: 'center', maxWidth: '92vw',
                }}
            >
                <span style={{ marginRight: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {undoable.leyenda || `Repuesto: ${undoable.name}`}
                </span>
                {/* Colores explícitos: el `btn-outline-light` del tema deja el texto del color del
                    body sobre el fondo negro del toast, y el botón se ve como un rectángulo vacío.
                    Un "Deshacer" ilegible es lo mismo que no tener deshacer. */}
                <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                        minHeight: '36px', color: '#fff', border: '1px solid #fff',
                        background: 'transparent', fontWeight: 'bold',
                    }}
                    onClick={this.deshacer}
                >
                    Deshacer
                </button>
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const { summary, loading, error } = this.state;
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={46} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Productos" subtitle="Reponer en estantería" />
                            {error && <div className="alert alert-danger">{error}</div>}

                            <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
                                <div>
                                    {summary && summary.products > 0 ? (
                                        <span style={{ fontSize: '16px' }}>
                                            <strong>{summary.products}</strong> {summary.products === 1 ? 'juego' : 'juegos'} para reponer
                                            {summary.units > summary.products ? <span className="text-muted"> · {summary.units} unidades</span> : null}
                                            {summary.overdue > 0 ? <span className="text-danger"> · {summary.overdue} atrasados</span> : null}
                                        </span>
                                    ) : null}
                                </div>
                                <button type="button" className="btn btn-sm btn-white" onClick={this.toggleDone}>
                                    {this.state.showDone ? 'Ocultar lo de hoy' : 'Ver lo resuelto hoy'}
                                </button>
                            </div>

                            <p className="text-muted" style={{ marginTop: '-6px' }}>
                                Lo que se vendió en el mostrador y todavía no volvió a la estantería, de lo más
                                reciente a lo más viejo. Traé la copia del depósito y marcala como repuesta.
                            </p>

                            <div className="card card-small mb-4">
                                {loading ? (
                                    <p className="p-3 m-0">Cargando…</p>
                                ) : this.accionables().length === 0 ? this.drawEmpty() : (
                                    <div>
                                        {this.grupos().map((grupo) => (
                                            <div key={grupo.date}>
                                                <div
                                                    className="px-3 py-2"
                                                    style={{ background: '#f5f6f8', borderBottom: '1px solid #e1e5eb', fontSize: '13px', textTransform: 'uppercase', color: '#5a6169', letterSpacing: '0.4px' }}
                                                >
                                                    {dayLabel(grupo.date)}
                                                </div>
                                                {grupo.items.map(this.drawItem)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {this.drawEsperandoStock()}
                            {this.drawDoneToday()}
                        </div>
                        {this.drawUndoToast()}
                    </main>
                </div>
            </div>
        );
    }
}

export default ShelfRestockPage;
